import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilePdf, 
  faLink, 
  faCheck, 
  faSpinner,
  faChevronUp,
  faChevronDown,
  faEye,
  faCalendarAlt,
  faMusic,
  faVideo,
  faQuoteLeft,
  faChartLine,
  faEnvelope,
  faPhone,
  faGlobe,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { 
  faSpotify, 
  faApple, 
  faSoundcloud, 
  faYoutube, 
  faInstagram, 
  faFacebook, 
  faTwitter, 
  faTiktok,
  faLinkedin
} from '@fortawesome/free-brands-svg-icons';
import html2pdf from 'html2pdf.js';
import styles from './EPKBuilder.module.css';

const PreviewStep = ({ data, mode, onExportPDF, onShareableLink, onPublish }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  
  // All sections expanded by default
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    photos: true,
    bio: true,
    music: true,
    videos: true,
    performances: true,
    stats: true,
    contact: true
  });
  
  // Determine if background color is dark (to use white text)
  const [isDarkBackground, setIsDarkBackground] = useState(false);
  
  // Create a reference to the EPK container for PDF generation
  const epkRef = React.useRef(null);
  
  // Function to determine if a color is dark
  const isColorDark = (hexColor) => {
    // Default to light if no color provided
    if (!hexColor) return false;
    
    // Convert hex to RGB
    let r = 0, g = 0, b = 0;
    
    // 3 digits
    if (hexColor.length === 4) {
      r = parseInt(hexColor[1] + hexColor[1], 16);
      g = parseInt(hexColor[2] + hexColor[2], 16);
      b = parseInt(hexColor[3] + hexColor[3], 16);
    } 
    // 6 digits
    else if (hexColor.length === 7) {
      r = parseInt(hexColor.substring(1, 3), 16);
      g = parseInt(hexColor.substring(3, 5), 16);
      b = parseInt(hexColor.substring(5, 7), 16);
    }
    
    // Calculate brightness (0-255)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Consider a brightness less than 128 as dark
    return brightness < 128;
  };
  
  // Effect to check background color darkness on mount and when theme changes
  useEffect(() => {
    const backgroundColor = data.theme?.backgroundColor || '#FFFFFF';
    setIsDarkBackground(isColorDark(backgroundColor));
  }, [data.theme]);
  
  // Function to toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Function to format location
  const formatLocation = () => {
    const parts = [];
    if (data.location?.city) parts.push(data.location.city);
    if (data.location?.state) parts.push(data.location.state);
    if (data.location?.country) parts.push(data.location.country);
    return parts.join(', ');
  };
  
  // Format numbers for stats display
  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat().format(num);
  };
  
  // Function to handle PDF export
  const handleExportPDF = async () => {
    if (isGeneratingPDF || mode === 'view') return;
    
    setIsGeneratingPDF(true);
    try {
      // Make sure all sections are expanded for PDF generation
      const previousExpandedState = {...expandedSections};
      setExpandedSections({
        header: true,
        photos: true,
        bio: true,
        music: true,
        videos: true,
        performances: true,
        stats: true,
        contact: true
      });
      
      // Wait a moment for the state update and rendering to complete
      setTimeout(() => {
        const element = epkRef.current;
        const pdfOptions = {
          margin: 10,
          filename: `${data.artistName || 'Artist'}_EPK.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };
        
        // Generate the PDF
        html2pdf().from(element).set(pdfOptions).save().then(() => {
          // Restore previous expanded sections state
          setExpandedSections(previousExpandedState);
          setIsGeneratingPDF(false);
        });
      }, 500);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setIsGeneratingPDF(false);
      alert('Error generating PDF. Please try again.');
    }
  };
  
  // Function to handle generating shareable link
  const handleShareableLink = () => {
    if (isGeneratingLink || mode === 'view') return;
    
    setIsGeneratingLink(true);
    try {
      onShareableLink();
    } catch (error) {
      console.error('Error generating shareable link:', error);
    } finally {
      setIsGeneratingLink(false);
    }
  };
  
  // Function to handle publishing
  const handlePublish = async () => {
    if (isPublishing || mode === 'view') return;
    
    setIsPublishing(true);
    try {
      await onPublish();
    } catch (error) {
      console.error('Error publishing EPK:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  // Get theme-specific class name
  const getThemeClassName = () => {
    const baseClass = styles.epkPreview;
    const themeTemplate = data.theme?.template || 'minimal';
    
    let themeClass = '';
    if (themeTemplate === 'visual') {
      themeClass = styles.visualTheme;
    } else if (themeTemplate === 'classic') {
      themeClass = styles.classicTheme;
    } else {
      themeClass = styles.minimalTheme;
    }
    
    return `${baseClass} ${themeClass} ${isDarkBackground ? styles.darkText : styles.lightText}`;
  };

  // Common section header style based on dark/light background
  const getSectionHeaderStyle = () => {
    return {
      color: isDarkBackground ? '#FFFFFF' : '#333333',
      backgroundColor: isDarkBackground ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
    };
  };
  
  return (
    <div className={styles.formSection}>
      <h2 className={styles.sectionTitle}>Preview & Publish</h2>
      <p className={styles.sectionDescription}>
        Review your EPK before publishing. You can always edit it later.
      </p>
      
      {mode !== 'view' && (
        <div className={styles.previewActions}>
          <button 
            className={`${styles.previewBtn} ${styles.previewPdfBtn}`}
            onClick={handleExportPDF}
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Generating PDF...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFilePdf} />
                Export as PDF
              </>
            )}
          </button>
          
          <button 
            className={`${styles.previewBtn} ${styles.previewLinkBtn}`}
            onClick={handleShareableLink}
            disabled={isGeneratingLink}
          >
            {isGeneratingLink ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Generating Link...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faLink} />
                Shareable Link
              </>
            )}
          </button>
          
          <button 
            className={`${styles.previewBtn} ${styles.previewPublishBtn}`}
            onClick={handlePublish}
            disabled={isPublishing}
          >
            {isPublishing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Publishing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} />
                Publish EPK
              </>
            )}
          </button>
        </div>
      )}
      
      {/* EPK Preview */}
      <div 
        ref={epkRef}
        className={getThemeClassName()}
        style={{ 
          '--accent-color': data.theme?.accentColor || '#E2384D',
          '--background-color': data.theme?.backgroundColor || '#FFFFFF',
          backgroundColor: data.theme?.backgroundColor || '#FFFFFF',
          color: isDarkBackground ? '#FFFFFF' : '#333333'
        }}
      >
        {/* Preview header */}
        <div className={styles.previewSection}>
          <div 
            className={styles.previewSectionHeader} 
            onClick={() => toggleSection('header')}
            style={getSectionHeaderStyle()}
          >
            <h3>Header Information</h3>
            <FontAwesomeIcon icon={expandedSections.header ? faChevronUp : faChevronDown} />
          </div>
          
          {expandedSections.header && (
            <div className={styles.previewSectionContent}>
              <div className={styles.epkHeader}>
                {data.logo && (
                  <div className={styles.epkLogo}>
                    <img src={data.logo.url} alt="Artist Logo" />
                  </div>
                )}
                
                <h1 className={styles.epkArtistName}>{data.artistName || 'Artist Name'}</h1>
                
                {data.artistType && (
                  <h2 className={styles.epkArtistType}>{data.artistType}</h2>
                )}
                
                {formatLocation() && (
                  <div className={styles.epkLocation}>
                    {formatLocation()}
                  </div>
                )}
                
                {data.genres && data.genres.length > 0 && (
                  <div className={styles.epkGenres}>
                    {data.genres.join(' • ')}
                  </div>
                )}
                
                {data.activeSince && (
                  <div className={styles.epkActiveSince}>
                    Active since {data.activeSince}
                  </div>
                )}
                
                {data.shortBio && (
                  <div className={styles.epkShortBio}>
                    {data.shortBio}
                  </div>
                )}
                
                {/* Social Links */}
                <div className={styles.epkSocialLinks}>
                  {data.socialLinks?.spotify && (
                    <div className={styles.epkSocialLink}>
                      <FontAwesomeIcon icon={faSpotify} />
                    </div>
                  )}
                  
                  {data.socialLinks?.appleMusic && (
                    <div className={styles.epkSocialLink}>
                      <FontAwesomeIcon icon={faApple} />
                    </div>
                  )}
                  
                  {data.socialLinks?.instagram && (
                    <div className={styles.epkSocialLink}>
                      <FontAwesomeIcon icon={faInstagram} />
                    </div>
                  )}
                  
                  {data.socialLinks?.facebook && (
                    <div className={styles.epkSocialLink}>
                      <FontAwesomeIcon icon={faFacebook} />
                    </div>
                  )}
                  
                  {data.socialLinks?.twitter && (
                    <div className={styles.epkSocialLink}>
                      <FontAwesomeIcon icon={faTwitter} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Photos Preview */}
        {data.photos && data.photos.length > 0 && (
          <div className={styles.previewSection}>
            <div 
              className={styles.previewSectionHeader} 
              onClick={() => toggleSection('photos')}
              style={getSectionHeaderStyle()}
            >
              <h3>Photos</h3>
              <FontAwesomeIcon icon={expandedSections.photos ? faChevronUp : faChevronDown} />
            </div>
            
            {expandedSections.photos && (
              <div className={styles.previewSectionContent}>
                <div className={styles.epkPhotosGrid}>
                  {data.photos.map((photo, index) => (
                    <div key={`photo-${index}`} className={styles.epkPhoto}>
                      <img src={photo.url} alt={`Artist photo ${index + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Bio Preview */}
        {data.longBio && (
          <div className={styles.previewSection}>
            <div 
              className={styles.previewSectionHeader} 
              onClick={() => toggleSection('bio')}
              style={getSectionHeaderStyle()}
            >
              <h3>Biography</h3>
              <FontAwesomeIcon icon={expandedSections.bio ? faChevronUp : faChevronDown} />
            </div>
            
            {expandedSections.bio && (
              <div className={styles.previewSectionContent}>
                <div className={styles.epkBio}>
                  {data.longBio}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Music Preview */}
        {((data.music?.spotify && data.music.spotify.length > 0) ||
          (data.music?.appleMusic && data.music.appleMusic.length > 0) ||
          (data.music?.soundcloud && data.music.soundcloud.length > 0)) && (
          <div className={styles.previewSection}>
            <div 
              className={styles.previewSectionHeader} 
              onClick={() => toggleSection('music')}
              style={getSectionHeaderStyle()}
            >
              <h3>
                <FontAwesomeIcon icon={faMusic} /> Music
              </h3>
              <FontAwesomeIcon icon={expandedSections.music ? faChevronUp : faChevronDown} />
            </div>
            
            {expandedSections.music && (
              <div className={styles.previewSectionContent}>
                <div className={styles.epkMusic}>
                  {data.music?.spotify && data.music.spotify.length > 0 && (
                    <div className={styles.epkEmbedContainer}>
                      <h4 style={{ color: isDarkBackground ? '#FFFFFF' : '#333333' }}>
                        <FontAwesomeIcon icon={faSpotify} /> Spotify
                      </h4>
                      <div className={styles.epkEmbeds}>
                        {data.music.spotify.slice(0, 1).map((track, index) => (
                          <div key={`spotify-preview-${index}`} className={styles.epkEmbed}>
                         <iframe
  src={`https://open.spotify.com/embed/track/${track.id}`}
  width="100%"
  height="80"
  frameBorder="0"
  allowtransparency="true" // Changed from allowTransparency to allowtransparency
  allow="encrypted-media"
  title={`Spotify Track Preview ${index + 1}`}
></iframe>

                          </div>
                        ))}
                        {data.music.spotify.length > 1 && (
                          <div className={styles.epkMoreItems}>
                            +{data.music.spotify.length - 1} more tracks
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Videos Preview */}
        {data.videos && data.videos.length > 0 && (
          <div className={styles.previewSection}>
            <div 
              className={styles.previewSectionHeader} 
              onClick={() => toggleSection('videos')}
              style={getSectionHeaderStyle()}
            >
              <h3>
                <FontAwesomeIcon icon={faVideo} /> Videos
              </h3>
              <FontAwesomeIcon icon={expandedSections.videos ? faChevronUp : faChevronDown} />
            </div>
            
            {expandedSections.videos && (
              <div className={styles.previewSectionContent}>
                <div className={styles.epkVideos}>
                  {data.videos.slice(0, 1).map((video, index) => (
                    <div key={`video-preview-${index}`} className={styles.epkVideo}>
                      <h4 style={{ color: isDarkBackground ? '#FFFFFF' : '#333333' }}>
                        {video.title || 'Featured Video'}
                      </h4>
                      <div className={styles.videoEmbedContainer}>
                        <iframe
                          width="100%"
                          height="315"
                          src={video.embedUrl}
                          title={video.title || `Video ${index + 1}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  ))}
                  {data.videos.length > 1 && (
                    <div className={styles.epkMoreItems}>
                      +{data.videos.length - 1} more videos
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Performances Preview */}
        {data.performances && data.performances.length > 0 && (
          <div className={styles.previewSection}>
            <div 
              className={styles.previewSectionHeader} 
              onClick={() => toggleSection('performances')}
              style={getSectionHeaderStyle()}
            >
              <h3>
                <FontAwesomeIcon icon={faCalendarAlt} /> Performance History
              </h3>
              <FontAwesomeIcon icon={expandedSections.performances ? faChevronUp : faChevronDown} />
            </div>
            
            {expandedSections.performances && (
              <div className={styles.previewSectionContent}>
                <div className={styles.epkPerformances}>
                  {data.performances.slice(0, 3).map((performance, index) => (
                    <div key={`perf-preview-${index}`} className={styles.epkPerformanceCard}>
                      <h4 className={styles.perfTitle}>{performance.name || `Performance ${index + 1}`}</h4>
                      <div className={styles.perfDetails}>
                        {performance.year && <span>{performance.year}</span>}
                        {performance.city && <span>{performance.city}</span>}
                      </div>
                      {performance.isHeadliner && (
                        <div className={styles.perfHeadliner}>Headliner</div>
                      )}
                    </div>
                  ))}
                  {data.performances.length > 3 && (
                    <div className={styles.epkMoreItems}>
                      +{data.performances.length - 3} more performances
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Stats & Press Preview */}
        {(data.stats || data.pressQuotes?.length > 0) && (
          <div className={styles.previewSection}>
            <div 
              className={styles.previewSectionHeader} 
              onClick={() => toggleSection('stats')}
              style={getSectionHeaderStyle()}
            >
              <h3>
                <FontAwesomeIcon icon={faChartLine} /> Stats & Press
              </h3>
              <FontAwesomeIcon icon={expandedSections.stats ? faChevronUp : faChevronDown} />
            </div>
            
            {expandedSections.stats && (
              <div className={styles.previewSectionContent}>
                <div className={styles.epkStats}>
                  {/* Stats Grid */}
                  {data.stats && (
                    <div className={styles.statsGrid}>
                      {data.stats.spotify?.monthlyListeners && (
                        <div className={styles.statCard}>
                          <div className={styles.statIcon}>
                            <FontAwesomeIcon icon={faSpotify} />
                          </div>
                          <div className={styles.statValue}>
                            {formatNumber(data.stats.spotify.monthlyListeners)}
                          </div>
                          <div className={styles.statLabel}>
                            Monthly Listeners
                          </div>
                        </div>
                      )}
                      
                      {data.stats.spotify?.totalStreams && (
                        <div className={styles.statCard}>
                          <div className={styles.statIcon}>
                            <FontAwesomeIcon icon={faSpotify} />
                          </div>
                          <div className={styles.statValue}>
                            {formatNumber(data.stats.spotify.totalStreams)}
                          </div>
                          <div className={styles.statLabel}>
                            Total Streams
                          </div>
                        </div>
                      )}
                      
                      {data.stats.youtube?.totalViews && (
                        <div className={styles.statCard}>
                          <div className={styles.statIcon}>
                            <FontAwesomeIcon icon={faYoutube} />
                          </div>
                          <div className={styles.statValue}>
                            {formatNumber(data.stats.youtube.totalViews)}
                          </div>
                          <div className={styles.statLabel}>
                            YouTube Views
                          </div>
                        </div>
                      )}
                      
                      {data.stats.instagram && (
                        <div className={styles.statCard}>
                          <div className={styles.statIcon}>
                            <FontAwesomeIcon icon={faInstagram} />
                          </div>
                          <div className={styles.statValue}>
                            {formatNumber(data.stats.instagram)}
                          </div>
                          <div className={styles.statLabel}>
                            Instagram Followers
                          </div>
                        </div>
                      )}
                      
                      {data.stats.tiktok && (
                        <div className={styles.statCard}>
                          <div className={styles.statIcon}>
                            <FontAwesomeIcon icon={faTiktok} />
                          </div>
                          <div className={styles.statValue}>
                            {formatNumber(data.stats.tiktok)}
                          </div>
                          <div className={styles.statLabel}>
                            TikTok Followers
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Press Quotes */}
                  {data.pressQuotes && data.pressQuotes.length > 0 && (
                    <div className={styles.pressQuotes}>
                      <h4 style={{ color: isDarkBackground ? '#FFFFFF' : '#333333', marginTop: '24px' }}>
                        <FontAwesomeIcon icon={faQuoteLeft} /> Press Quotes
                      </h4>
                      {data.pressQuotes.slice(0, 2).map((quote, index) => (
                        <div key={`quote-${index}`} className={styles.quoteCard}>
                          <div className={styles.quoteText}>
                            "{quote.quote}"
                          </div>
                          <div className={styles.quoteSource}>
                            — {quote.source}
                            {quote.link && (
                              <a 
                                href={quote.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={styles.quoteLink}
                              >
                                Read More
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Contact Preview */}
        {data.contact && (
          <div className={styles.previewSection}>
            <div 
              className={styles.previewSectionHeader} 
              onClick={() => toggleSection('contact')}
              style={getSectionHeaderStyle()}
            >
              <h3>Contact Information</h3>
              <FontAwesomeIcon icon={expandedSections.contact ? faChevronUp : faChevronDown} />
            </div>
            
            {expandedSections.contact && (
              <div className={styles.previewSectionContent}>
                <div className={styles.epkContact}>
                  {data.contact.name && (
                    <div className={styles.contactPerson}>
                      <div className={styles.contactName}>{data.contact.name}</div>
                      {data.contact.role && (
                        <div className={styles.contactRole}>{data.contact.role}</div>
                      )}
                    </div>
                  )}
                  
                  <div className={styles.contactInfo}>
                    {data.contact.email && (
                      <div className={styles.contactItem}>
                        <FontAwesomeIcon icon={faEnvelope} className={styles.contactIcon} />
                        <span>{data.contact.email}</span>
                      </div>
                    )}
                    
                    {data.contact.phone && (
                      <div className={styles.contactItem}>
                        <FontAwesomeIcon icon={faPhone} className={styles.contactIcon} />
                        <span>{data.contact.phone}</span>
                      </div>
                    )}
                    
                    {data.contact.website && (
                      <div className={styles.contactItem}>
                        <FontAwesomeIcon icon={faGlobe} className={styles.contactIcon} />
                        <span>{data.contact.website}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Footer */}
        <div className={styles.epkFooter}>
          <div className={styles.footerContent}>
            <div className={styles.epkPoweredBy}>
              Powered by OnTour EPK Builder
            </div>
            <div className={styles.epkTimestamp}>
              Last updated: {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Publication notice */}
      {mode !== 'view' && !data.isActive && (
        <div className={styles.publicationNotice}>
          <FontAwesomeIcon icon={faEye} className={styles.noticeIcon} />
          <p>
            Your EPK is currently in draft mode and is not publicly visible.
            Click "Publish EPK" to make it live and accessible via your profile.
          </p>
        </div>
      )}
      
      {mode !== 'view' && data.isActive && (
        <div className={styles.publicationNotice}>
          <FontAwesomeIcon icon={faCheck} className={styles.noticeIconActive} />
          <p>
            Your EPK is published and visible to the public.
            Any changes you make will be automatically saved and updated.
          </p>
        </div>
      )}
    </div>
  );
};

export default PreviewStep;